import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { TranslateHtml } from '@change-corgi/core-react/i18n';

import type { VideoFirstAskCopyArm } from '#src/app/pages/actions/shared/hooks';

import { MAX_DURATION_SEC } from '../../../VideoRecorder/components/RecorderTimeLimit';

import { AskActions } from './components/AskActions';
import { MontageVideo } from './components/MontageVideo';
import { useVideoFirstAsk } from './hooks/useVideoFirstAsk';
import { BoltIcon } from './icons';

const STAGGER_MS = 160;

const entranceDelay = (step: number): React.CSSProperties => ({ animationDelay: `${step * STAGGER_MS}ms` });

/**
 * HTML rather than a plain string because v3's copy carries an explicit `<br>`. Keys are literal here
 * rather than resolved in the hook so `no-invalid-translation-keys` can still check them.
 */
const Headline: React.FC<{ arm: VideoFirstAskCopyArm; location: string | undefined }> = ({ arm, location }) => {
	if (arm === 'v3_impact') {
		return <TranslateHtml value="corgi.petition_actions.video_first_ask.headline_impact" />;
	}

	if (location) {
		return (
			<TranslateHtml value="corgi.petition_actions.video_first_ask.headline_emotional" replacements={{ location }} />
		);
	}

	return <TranslateHtml value="corgi.petition_actions.video_first_ask.headline_emotional_no_location" />;
};

/**
 * Video-first ask screen, shared by `v1_emotional` and `v3_impact`. They differ only in the headline
 * and the supporting line - that isolation is the point of the experiment, so resist adding anything
 * else that branches on `arm`.
 */
export const VideoFirstAsk: React.FC<Props> = ({ arm }) => {
	const {
		data: { location, shouldAnimateEntrance },
		actions: { handleRecord, handleSkip },
	} = useVideoFirstAsk();

	// `motion-reduce` is required, not decorative: the keyframes' `both` fill-mode would otherwise hold
	// the elements invisible.
	const entrance = shouldAnimateEntrance && 'animate-rise-in motion-reduce:animate-none';

	return (
		<div className="bg-background-base -mx-4 -mb-8 flex flex-col" data-testid="video-first-ask">
			{/*
			 * Absolute against the page container, not this screen, so it reaches up behind the post-sign
			 * header. That is also why this screen renders outside the action card - the card is `relative`
			 * and `overflow-hidden`, so a gradient inside it could never clear the header.
			 */}
			<div className="bg-gradient-background-light-2 absolute inset-x-0 top-0 z-0 h-[311px]" aria-hidden>
				<div className="to-background-base absolute inset-x-0 bottom-0 h-[30%] bg-linear-to-b from-transparent" />
			</div>

			<div className="relative z-10 flex flex-col">
				<div className="flex flex-col items-center gap-2 px-10 pt-1 pb-2 text-center">
					<Text
						as="h1"
						variant="heading"
						// One token up from the design's original `medium` (VQA, 2026-08-31). It does not carry v3's
						// line break - the `<br>` in its copy does.
						size="large"
						fontWeight="bold"
						className={cn('text-typography-strong text-pretty', entrance)}
						style={shouldAnimateEntrance ? entranceDelay(0) : undefined}
					>
						<Headline arm={arm} location={location} />
					</Text>
					<Text
						as="p"
						variant="body"
						size="medium"
						className={cn('text-typography-strong w-[310px] max-w-full text-pretty', entrance)}
						style={shouldAnimateEntrance ? entranceDelay(1) : undefined}
					>
						{arm === 'v3_impact' ? (
							<TranslateHtml value="corgi.petition_actions.video_first_ask.supporting_line_impact" />
						) : (
							<TranslateHtml value="corgi.petition_actions.video_first_ask.supporting_line_emotional" />
						)}
					</Text>
				</div>
			</div>

			{/*
			 * The montage is out of flow so it can overlap the card below without a negative margin. The
			 * card's `pt-64` is what clears its height - the two have to move together.
			 */}
			<div
				className={cn('relative mx-auto flex w-[266px] max-w-full flex-col items-center', entrance)}
				style={shouldAnimateEntrance ? entranceDelay(2) : undefined}
			>
				{/*
				 * `bg-black` stops the montage flashing in. The player is client-only and paints nothing until
				 * Mux has its poster, so without a fill this box is a transparent hole and the scrim below
				 * smears over the page until the video lands.
				 */}
				<div className="elevation-elevated absolute top-0 z-20 h-[257px] w-[225px] overflow-hidden rounded-lg bg-black">
					<MontageVideo />
					<div className="pointer-events-none absolute inset-0 bg-linear-to-b from-transparent from-[59%] to-black" />
				</div>

				<div className="elevation-elevated bg-background-base relative z-10 mt-4 flex w-full flex-col items-center rounded-lg px-4 pt-64 pb-4 text-center">
					{/* Icon and label on one row rather than stacked, so the card needs less height. */}
					<div className="flex items-center justify-center gap-2">
						<BoltIcon className="h-3.5 w-3.5 shrink-0" />
						<Text as="p" variant="body" size="x-small" className="text-typography-strong">
							<TranslateHtml
								value="corgi.petition_actions.video_first_ask.value_prop"
								replacements={{ maxDuration: MAX_DURATION_SEC }}
							/>
						</Text>
					</div>
				</div>
			</div>

			{/*
			 * In flow, not pinned to the foot of the viewport: a fixed bottom bar eats the space shorter
			 * phones need for content (CHANGE-97687 review, 2026-08-25). `relative` only so it stacks above
			 * the gradient backdrop.
			 */}
			<div
				className={cn('relative z-10 mt-4 flex flex-col items-center px-4 pt-2 pb-5', entrance)}
				style={shouldAnimateEntrance ? entranceDelay(3) : undefined}
			>
				<AskActions onRecord={handleRecord} onSkip={handleSkip} />
			</div>
		</div>
	);
};

type Props = {
	arm: VideoFirstAskCopyArm;
};
